import { Box, Link, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/common/Button';
import DatePickerField from '../../../components/common/DatePickerField';
import Loading from '../../../components/common/Loading';
import SelectField from '../../../components/common/SelectField';
import TextField from '../../../components/common/TextField';
import LinkedinIcon from '../../../components/common/icons/LinkedinIcon';
import CustomTable from '../../../components/common/table/CustomTable';
import { Column } from '../../../components/common/table/types';
import ProfileDetailsPopup from '../../../components/module/rec/linkedin/ProfileDetailsPopup';
import { usePermissions } from '../../../context/PermissionContext';
import { useSidebar } from '../../../context/SidebarContext';
import api from '../../../utils/api';
import { formatDate } from '../../../utils/date';
import dayjs from 'dayjs';

interface LinkedinProfile {
  id: number;
  name: string;
  skills: string;
  company: string;
  experience: string;
  vacancy: string;
  email: string;
  country: string;
  synced_date: string;
  added_by: string;
  profile_url: string;
}

interface LinkedinProfileResponse {
  count: number;
  data: LinkedinProfile[];
}

interface FetchLinkedinProfilesParams {
  name?: string;
  skills?: string;
  company?: string;
  experience?: string;
  vacancy?: string;
  email?: string;
  country?: string;
  synced_date?: string;
  added_by?: string;
  page: number;
  limit: number;
  sort?: string;
}

const LinkedinSearch: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { updateMenu } = useSidebar();
  const [open, setOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const { hasPermission, pageId, setPageId } = usePermissions();
  const [profiles, setProfiles] = useState<LinkedinProfile[]>([]);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [sortField, setSortField] = useState<keyof LinkedinProfile | undefined>('name');
  const [searchParams, setSearchParams] = useState({
    name: '',
    skills: '',
    company: '',
    experience: '',
    vacancy: '',
    email: '',
    country: '',
    synced_date: '',
    added_by: '',
  });

  const fetchLinkedinProfiles = async (params: FetchLinkedinProfilesParams) => {
    try {
      setIsLoading(true);
      const response = await api.get<LinkedinProfileResponse>(
        '/rec/profile/search',
        {
          params: {
            ...params,
            sort: sortField ? `${sortField}:${sortDirection}` : undefined
          },
          headers: {
            'x-api-id': 3,
            'x-page-id': 6,
            'x-token': 'PSBrwdyDEe4tP8E1X89lEjeD3e7IpebrFtypPjwFD9S7baxiRhIxP5dyLJuqrxV0'
          }
        }
      );
      setProfiles(response.data.data);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error fetching LinkedIn profiles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pageId !== 8) {
      setPageId(8);
      return;
    }

    fetchLinkedinProfiles({
      name: searchParams.name ? searchParams.name : undefined,
      skills: searchParams.skills ? searchParams.skills : undefined,
      company: searchParams.company ? searchParams.company : undefined,
      experience: searchParams.experience ? searchParams.experience : undefined,
      vacancy: searchParams.vacancy ? searchParams.vacancy : undefined,
      email: searchParams.email ? searchParams.email : undefined,
      country: searchParams.country ? searchParams.country : undefined,
      synced_date: searchParams.synced_date ? searchParams.synced_date : undefined,
      added_by: searchParams.added_by ? searchParams.added_by : undefined,
      page: page + 1, // API uses 1-based pagination
      limit: rowsPerPage
    });
  }, [
    page,
    rowsPerPage,
    sortField,
    sortDirection,
    searchParams.name,
    searchParams.skills,
    searchParams.company,
    searchParams.experience,
    searchParams.vacancy,
    searchParams.email,
    searchParams.country,
    searchParams.synced_date,
    searchParams.added_by
  ]);

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
  };

  const handleReset = () => {
    setSearchParams({
      name: '',
      skills: '',
      company: '',
      experience: '',
      vacancy: '',
      email: '',
      country: '',
      synced_date: '',
      added_by: '',
    });
    setPage(0);
  };

  const handleSort = (field: keyof LinkedinProfile) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddApplicant = (profile: LinkedinProfile) => {
    setPageId(10);
    navigate('/rec/applicant/add', {
      state: {
        initialData: {
          vacancyCode: profile.vacancy,
          vacancy: profile.vacancy,
          source: 'linkedin',
          id: profile.id
        }
      }
    });
    updateMenu(2);
  };

  const columns: Column<LinkedinProfile>[] = [
    {
      field: 'name' as keyof LinkedinProfile, headerName: 'Name', width: '220px', sortable: true, type: 'text',
      renderCell: (row: LinkedinProfile) => (
        <div onClick={() => {
          setSelectedProfileId(row.id);
          setOpen(true);
        }} style={{ cursor: 'pointer' }}>{row.name}</div>
      )
    },
    { field: 'company' as keyof LinkedinProfile, headerName: 'Company', width: '200px', sortable: true, type: 'text', renderCell: (row: LinkedinProfile) => row.company },
    { field: 'experience' as keyof LinkedinProfile, headerName: 'Experience', width: '100px', sortable: true, type: 'text' },
    { field: 'vacancy' as keyof LinkedinProfile, headerName: 'Vacancy', width: '170px', sortable: true, type: 'text' },
    { field: 'email' as keyof LinkedinProfile, headerName: 'Email', width: '50px', sortable: true, type: 'text' },
    { field: 'country' as keyof LinkedinProfile, headerName: 'Country', width: '40px', sortable: true, type: 'text' },
    { field: 'synced_date' as keyof LinkedinProfile, headerName: 'Synced Date', width: '100px', sortable: true, type: 'date', renderCell: (row) => formatDate(row.synced_date) },
    { field: 'added_by' as keyof LinkedinProfile, headerName: 'Added By', width: '80px', sortable: true, type: 'text' },
    {
      field: 'actions' as keyof LinkedinProfile | 'actions',
      headerName: '',
      width: '80px',
      sortable: false,
      renderCell: (row: LinkedinProfile) => (
        <Box display="flex" gap="16px">
          <Link href={`${row.profile_url}`} target="_blank" style={{ textDecoration: 'none' }}>
            <LinkedinIcon />
          </Link>
          {hasPermission(1) && (
            <Button
              variant='contained'
              onClick={() => handleAddApplicant(row)}
              style={{ height: '20px', padding: '4px 8px', fontSize: '12px', width: '102px' }}
            >
              Add Applicant
            </Button>
          )}
        </Box>
      ),
    },
  ];

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box className="page-container" sx={{ py: '0 !important' }}>
      <Box className="search-section">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '24px' }}>
          <Typography className="page-title">
            Linkedin Search
          </Typography>
        </Box>
        <Box className="search-bar">
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Name"
              value={searchParams.name}
              onChange={(e) => setSearchParams({ ...searchParams, name: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Skills"
              value={searchParams.skills}
              onChange={(e) => setSearchParams({ ...searchParams, skills: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Company"
              value={searchParams.company}
              onChange={(e) => setSearchParams({ ...searchParams, company: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectField
              placeholder="Experience"
              value={searchParams.experience}
              onChange={(e) => setSearchParams({ ...searchParams, experience: e.target.value as string })}
              options={[
                { value: '0-2', label: '0-2 Years' },
                { value: '2-5', label: '2-5 Years' },
                { value: '5+', label: '5+ Years' },
              ]}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Vacancy"
              value={searchParams.vacancy}
              onChange={(e) => setSearchParams({ ...searchParams, vacancy: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Email"
              value={searchParams.email}
              onChange={(e) => setSearchParams({ ...searchParams, email: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Country"
              value={searchParams.country}
              onChange={(e) => setSearchParams({ ...searchParams, country: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <DatePickerField
              placeholder="Synced"
              value={searchParams.synced_date ? dayjs(searchParams.synced_date) : null}
              onChange={(date) => setSearchParams({ ...searchParams, synced_date: date || '' })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Added"
              value={searchParams.added_by}
              onChange={(e) => setSearchParams({ ...searchParams, added_by: e.target.value })}
              width="100%"
            />
          </div>
          <Box className="search-bar-button-group">
            <Button
              variant="contained"
              onClick={handleSearch}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
        </Box>
        {isLoading ? (
          <Loading message="Loading LinkedIn profiles..." />
        ) : (
          <CustomTable
            columns={columns}
            rows={profiles}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
          />
        )}
      </Box>

      <ProfileDetailsPopup
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedProfileId(null);
        }}
        profileId={selectedProfileId || 0}
      />
    </Box>
  );
};

export default LinkedinSearch;
